import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './FAQPage.css';

const FAQPage = () => {
  // Comprehensive FAQ data
  const faqData = [
    {
      category: "Retatrutide Questions",
      questions: [
        {
          q: "What is Retatrutide?",
          a: "Retatrutide (LY3437943) is a triple agonist peptide developed by Eli Lilly that targets GLP-1, GIP, and glucagon receptors. It has shown unprecedented weight loss of up to 24% in Phase 2 clinical trials, making it potentially the most effective weight loss medication in development."
        },
        {
          q: "How do I calculate Retatrutide dosage?",
          a: "Use our Retatrutide calculator by entering your vial size (typically 10mg), bacteriostatic water volume (1-2mL), and desired dose (1-12mg). The calculator will show you exactly how many units to draw in your insulin syringe. Start with 1mg weekly and titrate up gradually."
        },
        {
          q: "What is the typical Retatrutide dose?",
          a: "Clinical trials use a titration schedule: Week 1-4: 1mg, Week 5-8: 4mg, Week 9-12: 8mg, Week 13+: 12mg (maximum). Always start low and increase gradually to minimize side effects."
        },
        {
          q: "When will Retatrutide be FDA approved?",
          a: "Retatrutide is currently in Phase 3 trials (TRIUMPH program). Based on typical timelines, FDA approval is expected around 2026, though this could change based on trial results and regulatory review."
        },
        {
          q: "Retatrutide vs Tirzepatide - which is better?",
          a: "Retatrutide shows superior weight loss (24% vs 22.5%) compared to Tirzepatide in clinical trials. Retatrutide's triple agonist mechanism (adding glucagon) may provide additional metabolic benefits, but Tirzepatide is already FDA approved while Retatrutide is still in trials."
        }
      ]
    },
    {
      category: "Peptide Calculator Usage",
      questions: [
        {
          q: "What is a peptide calculator?",
          a: "A peptide calculator is a tool that helps determine the correct reconstitution ratio and dosage for injectable peptides. It calculates how much bacteriostatic water to add and how many units to draw in your syringe for your desired dose."
        },
        {
          q: "How do I use the peptide calculator?",
          a: "Enter three values: 1) Your peptide vial size in mg, 2) Amount of bacteriostatic water you'll add in mL, 3) Your desired dose in mg. The calculator will show the exact syringe volume to draw."
        },
        {
          q: "What syringe should I use for peptides?",
          a: "Most peptides use insulin syringes: 0.3mL (30 units) for small doses, 0.5mL (50 units) for medium doses, or 1mL (100 units) for standard doses. The 1mL syringe is most versatile and commonly used."
        },
        {
          q: "How much bacteriostatic water should I add?",
          a: "Common ratios: For a 10mg vial, add 1mL for 10mg/mL concentration (easier math) or 2mL for 5mg/mL concentration (more dilute, easier to measure small doses). More water = more dilute = easier to measure."
        }
      ]
    },
    {
      category: "Reconstitution & Storage",
      questions: [
        {
          q: "How do I reconstitute peptides?",
          a: "1) Clean vial top with alcohol, 2) Draw bacteriostatic water into syringe, 3) Insert needle at an angle, 4) Slowly inject water down the side of vial (not directly on powder), 5) Gently swirl (don't shake) until dissolved."
        },
        {
          q: "How long do reconstituted peptides last?",
          a: "Reconstituted peptides typically last 4-6 weeks when refrigerated (36-46°F). Some peptides like BPC-157 may last longer. Always check for cloudiness or particles before use."
        },
        {
          q: "Can I freeze peptides?",
          a: "Unreconstituted peptides can be frozen for long-term storage. Once reconstituted, do NOT freeze as this can damage the peptide structure. Keep refrigerated only."
        },
        {
          q: "What is bacteriostatic water?",
          a: "Bacteriostatic water is sterile water containing 0.9% benzyl alcohol as a preservative. It prevents bacterial growth and is specifically designed for reconstituting peptides for multiple-dose use."
        }
      ]
    },
    {
      category: "GLP-1 Agonists",
      questions: [
        {
          q: "What are GLP-1 agonists?",
          a: "GLP-1 agonists are medications that mimic the GLP-1 hormone, which regulates blood sugar and appetite. They include Semaglutide (Ozempic/Wegovy), Tirzepatide (Mounjaro), Liraglutide (Victoza/Saxenda), and the investigational Retatrutide."
        },
        {
          q: "Semaglutide vs Tirzepatide - which is more effective?",
          a: "Tirzepatide shows superior weight loss (up to 22.5%) compared to Semaglutide (up to 15-17%) in head-to-head trials. Tirzepatide's dual action (GLP-1 + GIP) provides additional benefits over Semaglutide's single GLP-1 action."
        },
        {
          q: "What are common GLP-1 side effects?",
          a: "Common side effects include nausea, diarrhea, constipation, vomiting, and decreased appetite. These usually improve over time. Rare but serious side effects include pancreatitis and gastroparesis."
        },
        {
          q: "How do I calculate Semaglutide dosage?",
          a: "For Semaglutide: Start at 0.25mg weekly for 4 weeks, then 0.5mg for 4 weeks, then 1mg, up to 2.4mg maximum for weight loss. Use our calculator to determine syringe units based on your concentration."
        }
      ]
    },
    {
      category: "Safety & Medical",
      questions: [
        {
          q: "Are peptides safe?",
          a: "FDA-approved peptides like Semaglutide and Tirzepatide have established safety profiles. Research peptides lack human safety data. Always consult a healthcare provider before using any peptide."
        },
        {
          q: "Can I mix different peptides?",
          a: "Generally, peptides should not be mixed in the same syringe unless specifically designed for combination. Use separate syringes and injection sites if using multiple peptides."
        },
        {
          q: "Where do I inject peptides?",
          a: "Most peptides are injected subcutaneously (under the skin) in the abdomen, thigh, or upper arm. Rotate injection sites to prevent lipodystrophy. Clean the area with alcohol before injection."
        },
        {
          q: "What if I miss a dose?",
          a: "For weekly peptides: If within 3 days of missed dose, take it immediately. If more than 3 days, skip and take next scheduled dose. Never double up doses."
        }
      ]
    }
  ];

  // Structured data for FAQ schema
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.flatMap(category => 
      category.questions.map(item => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a
        }
      }))
    )
  };

  return (
    <div className="faq-page">
      <Helmet>
        <title>Peptide Calculator FAQ - Retatrutide, Semaglutide & Dosage Questions | PeptideDose</title>
        <meta name="description" content="Frequently asked questions about peptide calculators, Retatrutide dosing, reconstitution, and GLP-1 agonists. Get answers about Semaglutide, Tirzepatide, and peptide safety." />
        <meta name="keywords" content="peptide calculator faq, retatrutide questions, how to calculate peptide dosage, bacteriostatic water calculator, GLP-1 side effects" />
        
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div className="faq-hero">
        <div className="hero-content">
          <Link to="/" className="back-link">← Back to Calculator</Link>
          <h1 className="faq-title">Frequently Asked Questions</h1>
          <p className="faq-subtitle">Everything you need to know about peptide calculators and dosing</p>
        </div>
      </div>

      {/* Quick Navigation */}
      <nav className="faq-nav">
        <div className="nav-container">
          {faqData.map((category, index) => (
            <a 
              key={index} 
              href={`#${category.category.toLowerCase().replace(/\s+/g, '-')}`}
              className="nav-link"
            >
              {category.category}
            </a>
          ))}
        </div>
      </nav>

      {/* FAQ Content */}
      <div className="faq-content">
        {faqData.map((category, categoryIndex) => (
          <section 
            key={categoryIndex}
            id={category.category.toLowerCase().replace(/\s+/g, '-')}
            className="faq-category"
          >
            <h2 className="category-title">{category.category}</h2>
            <div className="questions-list">
              {category.questions.map((item, questionIndex) => (
                <details key={questionIndex} className="faq-item">
                  <summary className="faq-question">
                    {item.q}
                  </summary>
                  <div className="faq-answer">
                    <p>{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* CTA Section */}
      <div className="faq-cta">
        <h2>Ready to Calculate Your Peptide Dose?</h2>
        <p>Use our free calculator to determine exact dosing for any peptide</p>
        <div className="cta-buttons">
          <Link to="/" className="cta-btn primary">
            Open Calculator
          </Link>
          <Link to="/retatrutide-calculator" className="cta-btn secondary">
            Retatrutide Calculator
          </Link>
        </div>
      </div>

      {/* Hidden SEO Content */}
      <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
        <h1>Peptide Calculator FAQ - Frequently Asked Questions</h1>
        <p>Complete FAQ guide for peptide calculators, Retatrutide dosing, Semaglutide calculations, Tirzepatide reconstitution, and GLP-1 agonist information.</p>
        <h2>How to Calculate Peptide Dosage</h2>
        <p>Learn how to use a peptide calculator for accurate dosing of Retatrutide, Semaglutide, Tirzepatide, and other peptides.</p>
      </div>
    </div>
  );
};

export default FAQPage;